import React, { useState, useEffect } from 'react';
import { Loader2, UserCheck, MapPin, UserX } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

/**
 * Pick a loan executive for one lead or for a selected batch.
 * Shows each executive's cities and their current book so the manager can
 * balance the load instead of guessing.
 */
export const AssignExecutiveDialog = ({
  open, onOpenChange, executives = [], currentOwnerId = null, allowClear = false,
  leadCount = 1, customerName = '', busy = false, onConfirm,
}) => {
  const [picked, setPicked] = useState(currentOwnerId);

  useEffect(() => { if (open) setPicked(currentOwnerId); }, [open, currentOwnerId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="assign-executive-dialog">
        <DialogHeader>
          <DialogTitle>{(currentOwnerId || allowClear) ? 'Reassign' : 'Assign'} loan {leadCount > 1 ? 'leads' : 'lead'}</DialogTitle>
          <DialogDescription>
            {leadCount > 1
              ? `${leadCount} leads selected — they will all move to the executive you pick.`
              : (customerName || 'This lead') + ' will move to the executive you pick.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-[46vh] overflow-y-auto py-1">
          {executives.length === 0 && (
            <p className="text-sm text-slate-500 py-6 text-center" data-testid="no-executives-msg">
              No active loan executives found. Add the Loan Executive role to an employee first.
            </p>
          )}
          {executives.map((e) => (
            <button
              key={e.id}
              type="button"
              onClick={() => setPicked(e.id)}
              data-testid={`assign-option-${e.id}`}
              className={`w-full text-left p-3 rounded-lg border transition-all ${
                picked === e.id ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-slate-200 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{e.name}</p>
                  <p className="text-xs text-slate-500 flex items-center gap-1 truncate">
                    <MapPin className="h-3 w-3" />
                    {e.cities?.length ? e.cities.join(', ') : 'No cities assigned'}
                  </p>
                </div>
                <span className="shrink-0 text-xs font-semibold text-slate-600 bg-slate-100 rounded-full px-2.5 py-1">
                  {(e.counts?.all || 0).toLocaleString()} leads
                </span>
              </div>
            </button>
          ))}

          {(currentOwnerId || allowClear) && (
            <button
              type="button"
              onClick={() => setPicked('__none__')}
              data-testid="assign-option-none"
              className={`w-full text-left p-3 rounded-lg border transition-all ${
                picked === '__none__' ? 'border-rose-400 bg-rose-50' : 'border-slate-200 hover:bg-slate-50'
              }`}
            >
              <p className="text-sm font-medium text-rose-700 flex items-center gap-2">
                <UserX className="h-3.5 w-3.5" /> Remove the owner
              </p>
            </button>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="assign-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(picked === '__none__' ? null : picked)}
            disabled={busy || !picked || picked === currentOwnerId}
            data-testid="assign-confirm-btn"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserCheck className="h-4 w-4 mr-2" />}
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssignExecutiveDialog;
